import { renderCanvas } from './render.js'
import { MATCHUP_W, MATCHUP_H } from './layout.js'
import { makeViewport } from './viewport.js'
import { makeSeriesPopover } from './series.js'

// Mounts one bracket widget. Markup comes from tournaments/_bracket.html.erb.
export function mount(root) {
  if (root.dataset.jsBracketMounted) return root.bracket
  root.dataset.jsBracketMounted = '1'
  const mode = root.dataset.jsBracketMode || 'view'
  const viewportEl = root.querySelector('[data-js-bracket-viewport]')
  const canvas = root.querySelector('[data-js-bracket-canvas]')
  const nav = root.querySelector('[data-js-bracket-nav]')
  const pctEl = root.querySelector('[data-js-zoom-pct]')
  const dataEl = root.querySelector('[data-js-bracket-data]')
  let payload = JSON.parse(dataEl.textContent)
  const interactive = mode !== 'preview'
  const vp = makeViewport(viewportEl, canvas, { pctEl, interactive })
  const popover = interactive ? makeSeriesPopover(viewportEl, { manager: mode === 'manager' }) : null
  let layout = null

  // Matchups worth opening on: the matchups being played, or the championship
  // game once there is a champion. Empty when the whole bracket should show.
  function focusKeys() {
    const matchups = Object.values(layout.matchupsByKey)
    const playable = matchups.filter(s => s.status === 'ready' || s.status === 'in_progress')
    if (playable.length) return playable.map(s => s.key)
    if (payload.champion && layout.matchupsByKey[payload.final]) return [payload.final]
    return []
  }

  // Initial framing. A bracket that fits at a readable size is shown whole;
  // a bigger one opens on the rounds in play, and "Fit" still shows it all.
  const READABLE = 0.8
  const LABEL_ROOM = 28 // the round names above the first matchup row
  function frame() {
    if (mode === 'preview') return vp.fitAll()
    const keys = mode === 'editor' ? [] : focusKeys()
    if (!keys.length || vp.fitScale() >= READABLE) return vp.fit()
    const boxes = keys.map(k => layout.abs[k]).filter(Boolean)
    const x1 = Math.min(...boxes.map(b => b.x)), y1 = Math.min(...boxes.map(b => b.y)) - LABEL_ROOM
    const x2 = Math.max(...boxes.map(b => b.x)) + MATCHUP_W, y2 = Math.max(...boxes.map(b => b.y)) + MATCHUP_H
    const rect = { x: x1, y: y1, w: x2 - x1, h: y2 - y1 }
    // In a split bracket the rounds in play sit on both edges; a region that
    // wide can't be framed, so show the bracket from its left edge instead.
    if (!vp.fitsAcross(rect)) return vp.fit()
    vp.fitRect(rect)
  }

  function draw({ refit = false } = {}) {
    layout = renderCanvas(canvas, payload, { editor: mode === 'editor' })
    vp.setSize(layout.width, layout.height)
    if (refit) frame()
    if (nav) {
      nav.hidden = layout.blocks.length < 2
      nav.innerHTML = layout.blocks.map(b => `<button type="button" data-js-goto="${b.bracket.key}">${b.bracket.name}</button>`).join('')
    }
    root.dispatchEvent(new CustomEvent('bracket:rendered', { detail: { payload, layout } }))
  }

  if (nav) nav.addEventListener('click', e => {
    const b = e.target.closest('[data-js-goto]')
    if (!b) return
    const blk = layout.blocks.find(x => x.bracket.key === b.dataset.jsGoto)
    if (blk) vp.panTo(0, blk.titleY - 24)
  })

  root.querySelector('[data-js-zoom-in]')?.addEventListener('click', () => vp.zoomIn())
  root.querySelector('[data-js-zoom-out]')?.addEventListener('click', () => vp.zoomOut())
  root.querySelector('[data-js-zoom-fit]')?.addEventListener('click', () => vp.fit())
  root.querySelector('[data-js-zoom-full]')?.addEventListener('click', () => vp.fullscreen())
  // Both of these live on document/window rather than on the widget, so they
  // outlive it unless they take themselves off. A league home page mounts one
  // widget per active tournament, and resize fires in bursts, so the redraw is
  // also coalesced to one per frame instead of one per event.
  const onFullscreenChange = () => {
    if (!root.isConnected) return document.removeEventListener('fullscreenchange', onFullscreenChange)
    if (document.fullscreenElement === viewportEl || !document.fullscreenElement) vp.fit()
  }
  document.addEventListener('fullscreenchange', onFullscreenChange)

  let resizeQueued = false
  const onResize = () => {
    if (!root.isConnected) return window.removeEventListener('resize', onResize)
    if (resizeQueued) return
    resizeQueued = true
    requestAnimationFrame(() => {
      resizeQueued = false
      if (root.isConnected) frame()
    })
  }
  window.addEventListener('resize', onResize)

  // One game goes straight to its page; a series (more than one game) shows
  // every result in the popover.
  function openMatchup(el) {
    const matchup = layout.matchupsByKey[el.dataset.jsOpenMatchup]
    if (!matchup || !matchup.games.length) return
    if (matchup.games.length === 1) {
      window.location.href = matchup.games[0].url
      return
    }
    popover.open(matchup, el)
  }

  if (interactive) {
    viewportEl.addEventListener('click', e => {
      const el = e.target.closest('[data-js-open-matchup]')
      if (!el) return
      e.preventDefault()
      openMatchup(el)
    })
    viewportEl.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      const el = e.target.closest('[data-js-open-matchup]')
      if (!el) return
      e.preventDefault()
      openMatchup(el)
    })
  }

  draw({ refit: true })

  const api = {
    root, viewportEl, canvas, vp,
    get payload() { return payload },
    get layout() { return layout },
    setPayload(next, opts) { payload = next; draw({ refit: true, ...opts }) },
    redraw() { draw() }
  }
  root.bracket = api
  return api
}

export function mountAll() {
  document.querySelectorAll('[data-js-bracket]').forEach(mount)
}
